import { useTranslation } from "@redotech/redo-i18n";
import { InteractiveMapLocation } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useQuery } from "@tanstack/react-query";
import { memo, useState } from "react";
import { InteractiveMap } from "../in-store-locations";

export interface ReturnBearLocationsSearchParams {
  draftReturnId?: string;
  returnId?: string;
}

const ReturnBearLocationsModal = memo(function ReturnBearLocationsModal({
  pending,
  error,
  locations,
}: {
  pending: boolean;
  error: boolean;
  locations: InteractiveMapLocation[] | null;
}) {
  const { t } = useTranslation();

  if (pending) {
    return <LoadingRedoAnimation />;
  }

  if (error) {
    return (
      <Text textColor="error">{t("Error loading Return Bear locations")}</Text>
    );
  }

  if (!locations) {
    return null;
  }

  return <InteractiveMap locations={locations} />;
});

export const ReturnBearLocationsButton = memo(
  function ReturnBearLocationsButton({
    draftReturnId,
    returnId,
    autoOpen = false,
    searchReturnBearLocations,
  }: {
    draftReturnId?: string;
    returnId?: string;
    autoOpen?: boolean;
    searchReturnBearLocations: (
      params: ReturnBearLocationsSearchParams,
    ) => Promise<InteractiveMapLocation[]>;
  }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(autoOpen);
    const query = useQuery({
      queryKey: ["returnBearLocations", draftReturnId, returnId],
      queryFn: () => searchReturnBearLocations({ draftReturnId, returnId }),
      enabled: isOpen,
      staleTime: Infinity,
    });

    return (
      <Flex>
        <RedoButton
          hierarchy="secondary"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
          text={t("See available locations")}
        />
        <RedoModal
          isOpen={isOpen}
          onModalCloseRequested={() => setIsOpen(false)}
          title={t("Find Return Bear drop-off locations")}
        >
          <ReturnBearLocationsModal
            error={!!query.error}
            locations={query.data ?? null}
            pending={query.isPending && query.fetchStatus !== "idle"}
          />
        </RedoModal>
      </Flex>
    );
  },
);
